import { useState, useEffect, useRef, memo, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import PageLayout from "@/components/PageLayout";
import Footer from "@/components/Footer";
import FloatingContactBar from "@/components/FloatingContactBar";
import {
  Phone,
  MessageSquare,
  ShieldCheck,
  Clock,
  Settings,
  Box,
  Zap,
  CheckCircle2,
  Tag,
  Award,
  Users,
  MapPin,
  Plus,
  Check,
  X,
  ExternalLink,
  ChevronUp,
  Sparkles,
  Building2,
  Tent,
  CalendarDays,
} from "lucide-react";

const SCRIPT_URL =
  "https://script.google.com/macros/s/AKfycby5HKEZNMObZRaeTidKlOMCLR_2YpgZuP2jOscP-OHWz2pi8PT0j8giTglCwm0VO5_J/exec";

const HERO_IMAGE =
  "https://res.cloudinary.com/dizdlq9fo/image/upload/v1779341284/Artboard_16_copy_2x_kc9olu.webp";

const MOBILE_HERO_IMAGE =
  "https://res.cloudinary.com/dizdlq9fo/image/upload/v1775290753/Artboard_19_wak3xm.webp";

const partners = [
  { name: "Dubai Petroleum", style: "text-2xl md:text-4xl font-black text-white" },
  { name: "Centaur", style: "text-xl md:text-3xl font-light text-white/40" },
  { name: "Schlumberger", style: "text-2xl md:text-4xl font-bold text-white/70" },
  { name: "Emirates", style: "text-xl md:text-3xl font-medium text-red-600/80" },
  { name: "Caterpillar", style: "text-2xl md:text-4xl font-semibold text-white/50" },
  { name: "Gitex", style: "text-2xl md:text-4xl font-black text-white" },
  { name: "Gulf Food", style: "text-xl md:text-3xl font-light text-white/40" },
  { name: "Adnoc", style: "text-2xl md:text-4xl font-bold text-white/70" },
  { name: "Dubai Air Show", style: "text-xl md:text-3xl font-medium text-red-600/80" },
  { name: "Dubai World Trade Centre", style: "text-2xl md:text-4xl font-semibold text-white/50" },
  { name: "North Star", style: "text-2xl md:text-4xl font-black text-white" },
  { name: "Xverse", style: "text-xl md:text-3xl font-light text-white/40" },
  { name: "Tesla", style: "text-2xl md:text-4xl font-bold text-white/70" },
  { name: "Dubai World Cup", style: "text-xl md:text-3xl font-medium text-red-600/80" },
  { name: "SML", style: "text-2xl md:text-4xl font-black text-white" },
  { name: "Larsen & Toubro", style: "text-xl md:text-3xl font-light text-white/40" },
  { name: "Lamprell", style: "text-2xl md:text-4xl font-bold text-white/70" },
  { name: "Archidon", style: "text-xl md:text-3xl font-medium text-red-600/80" },
  { name: "Shoba Construction", style: "text-2xl md:text-4xl font-semibold text-white/50" },
  { name: "Amaal", style: "text-2xl md:text-4xl font-black text-white" },
  { name: "Marwan", style: "text-xl md:text-3xl font-light text-white/40" },
  { name: "Shoba", style: "text-2xl md:text-4xl font-bold text-white/70" },
  { name: "Emirates Gold Pavilion", style: "text-2xl md:text-4xl font-black text-white" },
  { name: "Dubai World Cup", style: "text-xl md:text-3xl font-light text-white/40" },
  { name: "World Teakwabdo Championship", style: "text-2xl md:text-4xl font-bold text-white/70" },
  { name: "Football Central", style: "text-xl md:text-3xl font-medium text-red-600/80" },
  { name: "FIE Equestrian", style: "text-2xl md:text-4xl font-semibold text-white/50" },
];

const productData = [
  { title: "Events & Wedding", path: "/events", isClickable: true, tag: "TENTS FOR RENTAL & SALE", description: "Premium event tents, trade show halls, launch spaces, and temporary venues built for high-impact brand experiences.", image: "https://res.cloudinary.com/dizdlq9fo/image/upload/v1779341403/Artboard_5_klgd2k.webp" },
  { title: "Warehousing", path: "/warehousing", isClickable: true, tag: "TENTS FOR RENTAL & SALE", description: "Clear-span warehouse tents for logistics, industrial storage, inventory overflow, and rapid expansion requirements.", image: "https://res.cloudinary.com/dizdlq9fo/image/upload/v1775290753/Artboard_19_wak3xm.webp" },
  { title: "Labour Rest Area", path: "/labour", isClickable: true, tag: "TENTS FOR RENTAL & SALE", description: "Comfortable, durable, and quickly deployed rest-area structures for large workforces and project sites.", image: "https://res.cloudinary.com/dizdlq9fo/image/upload/v1775289717/Artboard_35_copy_nkwyiw.webp" },
  { title: "Sales Center", path: "/sales", isClickable: true, tag: "TENTS FOR RENTAL & SALE", description: "Modern temporary sales offices, real estate showrooms, reception areas, and branded customer-facing spaces.", image: "https://res.cloudinary.com/dizdlq9fo/image/upload/v1775289718/Artboard_35_copy_2_tpvlnm.webp" },
  { title: "Wedding", isClickable: false, tag: "TENTS FOR RENTAL & SALE", description: "Elegant large-span tents for weddings, receptions, private celebrations, and premium hospitality setups.", image: "https://res.cloudinary.com/dizdlq9fo/image/upload/v1775290753/Artboard_25_ca9wxx.webp" },
  { title: "Healthcare Facilities", isClickable: false, tag: "TENTS FOR RENTAL & SALE", description: "Rapid-deployment healthcare shelters for screening, emergency response, vaccination, and temporary medical support.", image: "https://res.cloudinary.com/dizdlq9fo/image/upload/v1775290753/Artboard_26_zmhpdu.webp" },
  { title: "Ramadan Dining", isClickable: false, tag: "TENTS FOR RENTAL & SALE", description: "Spacious dining tents for Ramadan, community meals, staff catering, and hospitality operations.", image: "https://res.cloudinary.com/dizdlq9fo/image/upload/v1775290755/Artboard_24_eakba9.webp" },
  { title: "Consolation Tents", isClickable: false, tag: "TENTS FOR RENTAL & SALE", description: "Respectful and practical condolence tents with layouts suitable for comfort, privacy, and guest flow.", image: "https://res.cloudinary.com/dizdlq9fo/image/upload/v1775290752/Artboard_23_wu2lho.webp" },
  { title: "Exhibitions & Trade Fairs", isClickable: false, tag: "TENTS FOR RENTAL & SALE", description: "Customizable temporary halls for exhibitions, fairs, product displays, and high-traffic commercial events.", image: "https://res.cloudinary.com/dizdlq9fo/image/upload/v1775289717/Artboard_27_dlopra.webp" },
  { title: "Festivals & Cultural Gatherings", isClickable: false, tag: "TENTS FOR RENTAL & SALE", description: "Large venue structures for festivals, cultural programs, public gatherings, and seasonal attractions.", image: "https://res.cloudinary.com/dizdlq9fo/image/upload/v1775289718/Artboard_28_b6jkmp.webp" },
  { title: "Greenhouse & Gardening", isClickable: false, tag: "TENTS FOR RENTAL & SALE", description: "Protected greenhouse and gardening structures designed for nurseries, agriculture, and controlled growing areas.", image: "https://res.cloudinary.com/dizdlq9fo/image/upload/v1775289718/Artboard_30_fnbu0r.webp" },
  { title: "Registration Centers", isClickable: false, tag: "TENTS FOR RENTAL & SALE", description: "Efficient temporary registration and processing centers for visitors, staff, events, and public services.", image: "https://res.cloudinary.com/dizdlq9fo/image/upload/v1775289733/Artboard_22_tn0jho.webp" },
  { title: "Sports Facilities", isClickable: false, tag: "TENTS FOR RENTAL & SALE", description: "High-clearance sports tents and pavilion setups for courts, training zones, spectator areas, and team facilities.", image: "https://res.cloudinary.com/dizdlq9fo/image/upload/v1775289718/Artboard_35_y12euv.webp" },
  { title: "Military & Defense", isClickable: false, tag: "TENTS FOR RENTAL & SALE", description: "Heavy-duty tactical shelters for field operations, temporary bases, storage, and command support.", image: "https://res.cloudinary.com/dizdlq9fo/image/upload/v1775289718/Artboard_31_gyir7n.webp" },
  { title: "Aviation Hangars", isClickable: false, tag: "TENTS FOR RENTAL & SALE", description: "Clear-span aviation shelters for aircraft storage, maintenance operations, and temporary hangar requirements.", image: "https://res.cloudinary.com/dizdlq9fo/image/upload/v1775289717/Artboard_29_ivgmlu.webp" },
  { title: "Equestrian Facilities", isClickable: false, tag: "TENTS FOR RENTAL & SALE", description: "Professional equestrian covers, stable areas, arenas, and temporary event structures for horse facilities.", image: "https://res.cloudinary.com/dizdlq9fo/image/upload/v1775289718/Artboard_34_nizmrz.webp" },
];

const solutionSpecs = [
  { label: "Solution Type", value: "Temporary & Permanent Tent Structures" },
  { label: "Available Options", value: "Rental | Sale | Custom Manufacturing" },
  { label: "Clear Span Width", value: "3m to 60m+ Based on Application" },
  { label: "Structure", value: "High-Strength Aluminium & Heavy-Duty Modular Systems" },
  { label: "Cover Options", value: "PVC Fabric | ABS Panel | Sandwich Panel | Glass Wall Options" },
  { label: "Add-ons", value: "AC, Flooring, Lighting, Doors, Partitions, Furniture & Branding" },
  { label: "Coverage", value: "Dubai, Abu Dhabi, Sharjah & UAE-Wide Installation" },
];

const chooseUsFeatures = [
  { icon: ShieldCheck, title: "Engineered Strength", desc: "Reliable tent systems designed for UAE weather and demanding project conditions." },
  { icon: Settings, title: "Custom Layouts", desc: "Flexible dimensions, accessories, branding, and fit-out options for each use case." },
  { icon: Clock, title: "Fast Deployment", desc: "Quick delivery and installation for urgent rental or project requirements." },
  { icon: Zap, title: "Complete Setup", desc: "Support for flooring, cooling, lighting, partitions, doors, and site accessories." },
  { icon: Building2, title: "Multiple Industries", desc: "Solutions for events, warehousing, labour areas, sports, hospitality, and more." },
  { icon: Box, title: "Rental or Sale", desc: "Choose short-term rental or long-term ownership based on your project plan." },
];

const TrustBar = () => {
  const items = [
    { icon: Award, title: "30+", sub: "Years Experience" },
    { icon: Users, title: "8000+", sub: "Projects Delivered" },
    { icon: Tag, title: "Rental & Sale", sub: "Flexible Options" },
    { icon: ShieldCheck, title: "Civil Defense", sub: "Compliant Tents" },
    { icon: MapPin, title: "UAE-Wide", sub: "Fast Installation" },
  ];

  return (
    <div className="grid grid-cols-2 sm:grid-cols-3 md:flex md:flex-nowrap items-start md:items-center gap-4 md:gap-6 mt-6 border-t border-white/10 pt-6">
      {items.map((item, i) => (
        <div key={i} className={`${i === 4 ? "col-span-2 md:col-span-1" : ""} flex items-start md:items-center gap-3 ${i === 3 ? "md:ml-4" : ""}`}>
          <div className="p-2 bg-purple-500/10 rounded-lg shrink-0">
            <item.icon className="w-5 h-5 text-[#bd6aff]" />
          </div>
          <div className="flex flex-col min-w-0">
            <span className="text-white font-bold text-sm leading-none whitespace-nowrap">{item.title}</span>
            <span className="text-zinc-200 text-[10px] uppercase tracking-wider font-bold mt-1.5">{item.sub}</span>
          </div>
        </div>
      ))}
    </div>
  );
};

const ProductCard = memo(({ tent, scrollToTop }: any) => {
  const optimizedImage = tent.image.replace(
    "/upload/",
    "/upload/w_900,c_scale,q_auto:good,f_auto/"
  );

  const cardContent = (
    <div className="group h-full p-[1px] rounded-3xl overflow-hidden bg-white/5 border border-white/10 hover:border-[#bd6aff]/40 transition-all duration-300">
      <div className="bg-zinc-950/70 h-full p-3 rounded-3xl flex flex-col">
        <div className="relative aspect-[4/3] rounded-2xl overflow-hidden bg-zinc-900">
          <img
            src={optimizedImage}
            alt={`${tent.title} - ${tent.tag}`}
            loading="lazy"
            decoding="async"
            width={700}
            height={525}
            className="w-full h-full object-cover transition-all duration-700 group-hover:scale-105"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/10 to-transparent" />
          <div className="absolute bottom-4 left-4 right-4 flex items-center justify-between gap-3">
            <span className="px-3 py-1.5 rounded-full border border-white/15 bg-black/40 backdrop-blur-md text-[9px] font-black uppercase tracking-[0.18em] text-[#bd6aff]">
              {tent.tag}
            </span>
            {tent.isClickable && <ExternalLink className="w-4 h-4 text-white/80" />}
          </div>
        </div>

        <div className="p-4 flex-1">
          <h3 className="text-xl font-black uppercase tracking-tight text-white mb-3 group-hover:text-[#bd6aff] transition-colors">
            {tent.title}
          </h3>
          <p className="text-zinc-400 text-xs leading-relaxed line-clamp-3">{tent.description}</p>
        </div>

        <div className="px-4 pb-4">
          <div className="flex items-center gap-2 text-[10px] font-black uppercase tracking-widest text-zinc-300">
            <CheckCircle2 className="w-4 h-4 text-[#bd6aff]" />
            Custom sizing available
          </div>
        </div>
      </div>
    </div>
  );

  if (!tent.isClickable) return <div className="h-full">{cardContent}</div>;

  return (
    <Link
      to={tent.path}
      onClick={scrollToTop}
      className="block h-full no-underline focus:outline-none focus:ring-2 focus:ring-[#bd6aff] rounded-3xl"
      aria-label={`View details for ${tent.title}`}
    >
      {cardContent}
    </Link>
  );
});

ProductCard.displayName = "ProductCard";

const Solutions = () => {
  const navigate = useNavigate();
  const quoteRef = useRef<HTMLDivElement>(null);
  const [showScrollTop, setShowScrollTop] = useState(false);
  const [isDesktop, setIsDesktop] = useState(false);
  const [geo, setGeo] = useState({ code: "+971", country: "AE" });
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    requirements: "",
  });

  useEffect(() => {
    document.title = "Tent Solutions UAE | Rental & Sale | BA Tents";

    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute(
        "content",
        "Explore BA Tents solutions for events, warehousing, sports facilities, labour rest areas, sales centers, Ramadan dining, exhibitions, and more across the UAE."
      );
    }

    const mediaQuery = window.matchMedia("(min-width: 768px)");
    const handleChange = (event: MediaQueryListEvent) => setIsDesktop(event.matches);
    const handleScroll = () => setShowScrollTop(window.scrollY > 600);

    setIsDesktop(mediaQuery.matches);
    mediaQuery.addEventListener("change", handleChange);
    window.addEventListener("scroll", handleScroll, { passive: true });
    window.scrollTo(0, 0);

    const controller = new AbortController();
    const geoTimeout = setTimeout(() => {
      fetch("https://ipapi.co/json/", { signal: controller.signal })
        .then((res) => res.json())
        .then((data) => {
          if (data.country_calling_code) {
            setGeo({
              code: data.country_calling_code.startsWith("+")
                ? data.country_calling_code
                : `+${data.country_calling_code}`,
              country: data.country_code || "AE",
            });
          }
        })
        .catch(() => {});
    }, 2500);

    return () => {
      controller.abort();
      clearTimeout(geoTimeout);
      mediaQuery.removeEventListener("change", handleChange);
      window.removeEventListener("scroll", handleScroll);
    };
  }, []);

  const handleMiniSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    navigate("/thankyou");

    fetch(SCRIPT_URL, {
      method: "POST",
      mode: "no-cors",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        ...formData,
        phone: `${geo.code} ${formData.phone}`,
        source: "Solutions Page Mini Form",
      }),
    }).catch(() => {});
  };

  const scrollToQuote = useCallback(() => {
    quoteRef.current?.scrollIntoView({
      behavior: "smooth",
      block: "center",
    });
  }, []);

  const scrollToTop = useCallback(() => window.scrollTo({ top: 0, behavior: "smooth" }), []);

  return (
    <PageLayout>
      <div className="min-h-screen bg-black text-white">
        <section className="relative min-h-[62vh] md:min-h-[90vh] w-screen left-1/2 right-1/2 -ml-[50vw] -mr-[50vw] -mt-28 md:-mt-12 overflow-hidden flex flex-col bg-black">
          <img
            src={HERO_IMAGE}
            className="hidden md:block absolute inset-0 w-full h-full object-cover z-0 opacity-45 scale-105"
            alt="BA Tents solutions UAE"
            style={{ objectPosition: "center top" }}
            width={1920}
            height={1080}
            loading="eager"
            fetchPriority="high"
            decoding="async"
          />

          <div className="absolute inset-0 hidden md:block bg-gradient-to-b from-black/55 via-black/20 to-black z-[1]" />
          <div className="absolute inset-0 hidden md:block bg-gradient-to-r from-black via-black/70 to-transparent z-[2]" />
          <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[650px] h-[650px] bg-purple-600/10 blur-[130px] rounded-full z-[3] pointer-events-none" />

          <div className="flex-1 relative z-10 flex flex-col items-center justify-center px-4 md:px-0">
            <div className="w-full max-w-[1440px] grid grid-cols-1 md:grid-cols-2 gap-8 items-center md:px-12">
              <div className="w-full mb-6 p-4 md:p-0 md:-ml-12 bg-transparent">
                <div className="mt-24 inline-flex items-center gap-2 px-3 py-1 rounded-full border border-white/20 bg-white/10 mb-4 backdrop-blur-md">
                  <Sparkles className="w-3.5 h-3.5 text-[#bd6aff]" />
                  <span className="text-[10px] font-bold uppercase tracking-widest text-white">
                    UAE-Wide <span className="text-purple-400">Tent Rental & Sale Solutions</span>
                  </span>
                </div>

                <h1 className="text-[34px] md:text-[70px] font-black text-white leading-[1.03] mb-5 tracking-tight uppercase">
                  Where Our <br />
                  <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#bd6aff] to-purple-400">
                    Solutions
                  </span>{" "}
                  <br />
                  <span className="text-[22px] md:text-[38px] text-white/90">Fit Across UAE</span>
                </h1>

                <div className="mb-8 max-w-2xl">
                  <p className="text-zinc-300 text-sm md:text-base font-normal leading-relaxed">
                    <span className="text-white font-bold">BA Tents</span> provides engineered tent structures for events, warehousing, labour rest areas, sports facilities, sales centers, dining, exhibitions, and specialized project setups across the UAE.
                  </p>
                </div>

                <div className="flex flex-row items-center gap-1 md:gap-2 -mt-2 mb-3 md:mb-6 w-full max-w-lg">
                  <button
                    onClick={scrollToQuote}
                    className="flex-1 py-4 bg-purple-500/10 md:bg-gradient-to-r from-[#8b31ff] to-[#bd6aff] border border-purple-400/40 md:border-0 rounded-xl text-white font-black text-[10px] md:text-xs tracking-wider flex items-center justify-center gap-1.5 uppercase backdrop-blur-md md:backdrop-blur-0 shadow-none md:shadow-[0_0_20px_rgba(139,49,255,0.3)] active:scale-95 transition-transform"
                  >
                    <Phone className="w-3.5 h-3.5 fill-black/20" />
                    <span>Get Quote</span>
                  </button>

                  <a
                    href="tel:+971588243660"
                    className="flex-1 py-4 bg-white/10 md:bg-white border border-white/20 md:border-0 rounded-xl text-white md:text-black font-black text-[10px] md:text-xs flex items-center justify-center gap-1.5 uppercase backdrop-blur-md md:backdrop-blur-0 active:scale-95 transition-transform"
                  >
                    <Phone className="w-3.5 h-3.5 fill-white md:fill-black/20 text-white md:text-black" />
                    <span>Call Now</span>
                  </a>

                  <a
                    href="https://wa.me/971588243660"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex-1 py-4 bg-emerald-500/10 md:bg-emerald-500 border border-emerald-500/40 md:border-0 rounded-xl text-emerald-400 md:text-white font-black text-[10px] md:text-xs flex items-center justify-center gap-1.5 uppercase backdrop-blur-md md:backdrop-blur-0 active:scale-95 transition-transform"
                  >
                    <MessageSquare className="w-3.5 h-3.5" />
                    <span>WhatsApp</span>
                  </a>
                </div>

                <div className="max-w-2xl">
                  <TrustBar />
                </div>

                {!isDesktop && (
                  <div className="relative w-full mt-8 -mb-9 overflow-hidden rounded-2xl bg-zinc-900 border border-white/10 shadow-2xl">
                    <img
                      src={MOBILE_HERO_IMAGE}
                      className="w-full h-auto object-cover"
                      alt="BA Tents solutions mobile gallery"
                      width={800}
                      height={1000}
                      loading="eager"
                      fetchPriority="high"
                      decoding="async"
                    />
                  </div>
                )}
              </div>

              <div className="hidden md:block" />
            </div>
          </div>
        </section>

        <section className="py-12 md:py-16 bg-black relative overflow-hidden border-t border-white/5">
          <div className="absolute inset-y-0 left-0 w-32 bg-gradient-to-r from-black to-transparent z-10" />
          <div className="absolute inset-y-0 right-0 w-32 bg-gradient-to-l from-black to-transparent z-10" />
          <div className="flex flex-col gap-4">
            <p className="text-center text-[10px] font-black text-zinc-300 uppercase tracking-[0.3em] mb-4">
              Trusted by Industry Leaders
            </p>

            <div className="flex overflow-hidden group">
              <div className="flex flex-nowrap gap-16 items-center whitespace-nowrap px-8 animate-[marquee_30s_linear_infinite]">
                {[...partners, ...partners].map((partner, idx) => (
                  <span key={idx} className={`${partner.style} tracking-tighter opacity-60 hover:opacity-100 transition-opacity uppercase`}>
                    {partner.name}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </section>

        <section className="pt-4 pb-10 md:py-12 max-w-7xl mx-auto px-6">
          <div className="grid lg:grid-cols-3 gap-4 items-stretch">
            <div className="lg:col-span-2 relative p-6 md:p-8 rounded-[2rem] bg-[#121212] border border-zinc-800/60 shadow-2xl flex flex-col justify-between">
              <div>
                <div className="flex items-center gap-3 mb-6 md:mb-8">
                  <div className="p-2 bg-purple-500/10 rounded-lg">
                    <Tent className="w-5 h-5 text-[#bd6aff]" />
                  </div>
                  <h2 className="text-xl font-black uppercase tracking-tighter text-white">
                    Solution <span className="text-purple-500">Capabilities</span>
                  </h2>
                </div>

                <table className="w-full text-left text-xs border-collapse">
                  <tbody>
                    {solutionSpecs.map((s, i) => (
                      <tr key={i} className="border-b border-zinc-800/50 last:border-0">
                        <td className="py-4 font-bold text-zinc-400 uppercase text-[9px] tracking-widest w-1/3">
                          {s.label}
                        </td>
                        <td className="py-4 text-zinc-200 font-medium text-sm">
                          {s.value}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            <div
              ref={quoteRef}
              className="relative p-6 md:p-8 rounded-3xl bg-[#0F0F0F] border-2 border-purple-900/60 shadow-[0_0_30px_rgba(139,49,255,0.15)] flex flex-col gap-4 overflow-hidden"
            >
              <div className="absolute -right-20 -top-20 w-56 h-56 bg-purple-600/10 blur-3xl rounded-full pointer-events-none" />
              <div className="relative flex flex-col gap-1 border-b-2 border-zinc-900 pb-5">
                <h2 className="text-xl font-bold text-white tracking-tighter uppercase">Quick Quote</h2>
                <span className="text-[9px] font-black text-purple-400 uppercase tracking-widest">
                  Immediate 15-Minute Response
                </span>
                <p className="text-zinc-400 text-xs leading-relaxed mt-2">
                  Share your use case, location, and size. We will guide you on rental, sale, and setup.
                </p>
              </div>

              <form className="relative space-y-4 pt-2" onSubmit={handleMiniSubmit}>
                <input
                  type="text"
                  placeholder="Full Name"
                  required
                  className="w-full bg-[#121212] border border-zinc-800 rounded-xl px-4 py-3.5 text-zinc-200 text-[11px] focus:outline-none focus:border-purple-600 transition-colors placeholder:text-zinc-500"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                />

                <input
                  type="email"
                  placeholder="Email Address"
                  required
                  className="w-full bg-[#121212] border border-zinc-800 rounded-xl px-4 py-3.5 text-zinc-200 text-[11px] focus:outline-none focus:border-purple-600 transition-colors placeholder:text-zinc-500"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                />

                <div className="relative">
                  <div className="absolute left-4 top-1/2 -translate-y-1/2 text-purple-400 text-[10px] font-black border-r border-zinc-800 pr-3">
                    {geo.code}
                  </div>
                  <input
                    type="tel"
                    required
                    placeholder="Mobile Number"
                    className="w-full bg-[#121212] border border-zinc-800 rounded-xl pl-16 pr-4 py-3.5 text-zinc-200 text-[11px] focus:outline-none focus:border-purple-600 transition-colors placeholder:text-zinc-500"
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  />
                </div>

                <textarea
                  placeholder="Tell us about your project requirements..."
                  className="w-full h-28 bg-[#121212] border border-zinc-800 rounded-xl px-4 py-3.5 text-zinc-200 text-[11px] focus:outline-none resize-none focus:border-purple-600 transition-colors placeholder:text-zinc-500"
                  value={formData.requirements}
                  onChange={(e) => setFormData({ ...formData, requirements: e.target.value })}
                />

                <button
                  type="submit"
                  className="w-full py-4 bg-gradient-to-r from-purple-600 to-purple-800 text-white font-black rounded-xl uppercase tracking-widest text-[11px] shadow-[0_10px_30px_rgba(139,49,255,0.2)] active:scale-95 transition-all hover:brightness-110"
                >
                  Send Request
                </button>
              </form>
            </div>
          </div>
        </section>

        <section className="pt-8 pb-16 md:py-20 max-w-7xl mx-auto px-6 border-t border-white/5">
          <div className="text-center mb-8 md:mb-14">
            <p className="text-[10px] font-black text-[#bd6aff] uppercase tracking-[0.3em] mb-4">Applications</p>
            <h2 className="text-3xl md:text-5xl font-black tracking-tight uppercase">
              Explore Our <span className="text-[#bd6aff]">Tent Solutions</span>
            </h2>
            <p className="text-zinc-400 text-xs md:text-sm max-w-3xl mx-auto mt-4 leading-relaxed">
              Select the right structure for your project. Click available categories for detailed pages, or request a quote for custom sizing and setup.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
            {productData.map((tent) => (
              <ProductCard key={tent.title} tent={tent} scrollToTop={scrollToTop} />
            ))}
          </div>

          <div className="text-center mb-12 mt-[80px]">
            <h2 className="text-3xl md:text-5xl font-black tracking-tight mb-4 uppercase">
              Why Choose <span className="text-[#bd6aff]">BA Tents?</span>
            </h2>
            <p className="text-zinc-300 text-sm max-w-2xl mx-auto uppercase tracking-widest">
              Complete tent setup support for rental and sale projects
            </p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
            {chooseUsFeatures.map((f, i) => (
              <div key={i} className="p-6 bg-zinc-900/50 border border-white/5 rounded-2xl flex flex-col items-center text-center hover:bg-zinc-900 transition-colors group">
                <f.icon className="w-8 h-8 text-[#bd6aff] mb-4 group-hover:scale-110 transition-transform" />
                <h3 className="text-[11px] font-bold mb-2 tracking-tight uppercase text-white">{f.title}</h3>
                <p className="text-zinc-300 text-[10px] leading-relaxed">{f.desc}</p>
              </div>
            ))}
          </div>
        </section>

        <section className="pb-24 max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            <div className="relative group p-[1px] rounded-3xl overflow-hidden bg-white/5 border border-white/10 flex flex-col">
              <div className="bg-zinc-900/40 p-8 h-full flex flex-col">
                <div className="flex items-center gap-3 mb-4">
                  <div className="p-2 bg-purple-500/10 rounded-lg">
                    <Tag className="w-5 h-5 text-[#bd6aff]" />
                  </div>
                  <h2 className="text-xl font-black uppercase tracking-tighter text-white">
                    Tent Solutions <span className="text-purple-500">Sale</span>
                  </h2>
                </div>

                <p className="text-zinc-400 text-xs leading-relaxed mb-8">
                  Factory-direct permanent and semi-permanent tent structures for long-term operations, branded spaces, industrial use, and project expansion.
                </p>

                <div className="mt-auto">
                  <ul className="space-y-3 mb-8">
                    {[
                      "Custom manufacturing and engineered sizing",
                      "Long-term ownership and asset value",
                      "PVC, panel, glass, and insulation options",
                      "Branding and permanent fit-out support",
                      "Civil Defense and project-compliant options",
                      "UAE-wide delivery and installation",
                    ].map((item, i) => (
                      <li key={i} className="flex items-center gap-3 text-[11px] text-zinc-300">
                        <CheckCircle2 className="w-4 h-4 text-[#bd6aff]" />
                        {item}
                      </li>
                    ))}
                  </ul>

                  <button onClick={scrollToQuote} className="w-full px-6 py-4 bg-white/5 hover:bg-[#bd6aff] border border-white/10 hover:border-[#bd6aff] rounded-xl text-white text-[10px] font-black uppercase tracking-widest transition-all duration-300 flex justify-center items-center gap-2 group">
                    Get Quote <Tag className="w-3 h-3 opacity-50 group-hover:opacity-100" />
                  </button>
                </div>
              </div>
            </div>

            <div className="relative group p-[1px] rounded-3xl overflow-hidden bg-white/5 border border-white/10 flex flex-col">
              <div className="bg-zinc-900/40 p-8 h-full flex flex-col">
                <div className="flex items-center gap-3 mb-4">
                  <div className="p-2 bg-purple-500/10 rounded-lg">
                    <Clock className="w-5 h-5 text-[#bd6aff]" />
                  </div>
                  <h2 className="text-xl font-black uppercase tracking-tighter text-white">
                    Tent Solutions <span className="text-purple-500">Rental</span>
                  </h2>
                </div>

                <p className="text-zinc-400 text-xs leading-relaxed mb-8">
                  Fast rental deployment for events, temporary storage, workforce areas, seasonal demand, exhibitions, and urgent project requirements.
                </p>

                <div className="mt-auto">
                  <ul className="space-y-3 mb-8">
                    {[
                      "Flexible short-term and monthly rental plans",
                      "Rapid installation and dismantling",
                      "Lighting, AC, flooring, and furniture add-ons",
                      "Maintenance and relocation support",
                      "Scalable setup based on project needs",
                      "Suitable for events, industry, and site operations",
                    ].map((item, i) => (
                      <li key={i} className="flex items-center gap-3 text-[11px] text-zinc-300">
                        <CheckCircle2 className="w-4 h-4 text-[#bd6aff]" />
                        {item}
                      </li>
                    ))}
                  </ul>

                  <button onClick={scrollToQuote} className="w-full px-6 py-4 bg-white/5 hover:bg-[#bd6aff] border border-white/10 hover:border-[#bd6aff] rounded-xl text-white text-[10px] font-black uppercase tracking-widest transition-all duration-300 flex justify-center items-center gap-2 group">
                    Get Quote <Clock className="w-3 h-3 opacity-50 group-hover:opacity-100" />
                  </button>
                </div>
              </div>
            </div>

            <div className="relative group p-[1px] rounded-3xl overflow-hidden bg-gradient-to-b from-[#bd6aff]/20 to-transparent border border-[#bd6aff]/30 lg:col-span-1 md:col-span-2">
              <div className="bg-zinc-950/80 p-8 h-full flex flex-col">
                <h2 className="text-lg font-black uppercase tracking-tight text-white mb-6 text-center">
                  Rental vs Sale Comparison
                </h2>

                <div className="space-y-1">
                  <div className="grid grid-cols-3 text-[10px] font-bold uppercase tracking-widest text-zinc-300 pb-2 border-b border-white/5">
                    <div>Feature</div>
                    <div className="text-center">Rental</div>
                    <div className="text-center">Sale</div>
                  </div>

                  {[
                    { f: "Short-Term Use", r: true, s: true },
                    { f: "Long-Term ROI", r: false, s: true },
                    { f: "Fast Lead Time", r: "Fast", s: "Custom" },
                    { f: "Maintenance", r: "Incl.", s: "Opt." },
                    { f: "Branding", r: "Opt.", s: "Opt." },
                    { f: "AC & Lighting", r: "Opt.", s: "Opt." },
                    { f: "Asset Ownership", r: false, s: true },
                    { f: "Relocation", r: "Easy", s: "Planned" },
                  ].map((row, i) => (
                    <div key={i} className="grid grid-cols-3 items-center py-2.5 border-t border-white/5">
                      <div className="text-[10px] font-bold text-zinc-300 uppercase leading-none">{row.f}</div>
                      <div className="flex justify-center">
                        {row.r === true ? <Check className="w-4 h-4 text-[#bd6aff]" /> : row.r === false ? <X className="w-4 h-4 text-zinc-600" /> : <span className="text-[9px] text-[#bd6aff] font-bold uppercase">{row.r}</span>}
                      </div>
                      <div className="flex justify-center">
                        {row.s === true ? <Check className="w-4 h-4 text-[#bd6aff]" /> : row.s === false ? <X className="w-4 h-4 text-zinc-600" /> : <span className="text-[9px] text-[#bd6aff] font-bold uppercase">{row.s}</span>}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="pb-32 max-w-5xl mx-auto px-6 relative">
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-purple-600/5 blur-[120px] rounded-full pointer-events-none" />

          <div className="text-center mb-12 relative z-10">
            <h2 className="text-3xl md:text-5xl font-black tracking-tight mb-4 uppercase text-white">
              Tent Solutions <span className="text-[#bd6aff]">FAQ</span>
            </h2>
            <p className="text-zinc-500 text-[10px] uppercase tracking-[0.3em]">
              Project Planning Insights
            </p>
          </div>

          <div className="space-y-4 relative z-10">
            {[
              {
                q: "Do you provide tent solutions for both rental and sale in UAE?",
                a: "Yes. BA Tents provides both rental and sale tent solutions across the UAE, including event tents, warehouse tents, labour rest area tents, sales centers, sports facilities, dining tents, and customized temporary structures.",
              },
              {
                q: "Can the tents be customized for different industries?",
                a: "Yes. Tent size, height, walling, flooring, AC, lighting, doors, partitions, branding, and accessories can be customized based on the application and site requirements.",
              },
              {
                q: "Which solution is best: rental or purchase?",
                a: "Rental is ideal for temporary projects, events, seasonal overflow, and short-term site requirements. Purchase is better for long-term use, permanent facilities, and projects where asset ownership provides better value.",
              },
              {
                q: "Do you handle installation and removal?",
                a: "Yes. BA Tents provides delivery, installation, dismantling, relocation, and maintenance support for rental and sale projects across Dubai, Abu Dhabi, Sharjah, and the wider UAE.",
              },
              {
                q: "Can you support urgent project requirements?",
                a: "Yes. Many standard rental structures can be deployed quickly depending on inventory, location, site readiness, and required accessories.",
              },
            ].map((item, idx) => (
              <div key={idx} className="group p-[1px] rounded-2xl bg-white/10 border border-white/5 hover:border-[#bd6aff]/40 transition-all duration-300 shadow-xl">
                <details className="group">
                  <summary className="flex items-center justify-between p-6 cursor-pointer list-none">
                    <span className="text-sm font-bold text-zinc-200 uppercase tracking-tight group-hover:text-white transition-colors">
                      {item.q}
                    </span>
                    <Plus className="w-4 h-4 text-[#bd6aff] group-open:rotate-45 transition-transform duration-300" />
                  </summary>
                  <div className="px-6 pb-6 text-zinc-400 text-xs leading-relaxed border-t border-white/5 pt-4">
                    {item.a}
                  </div>
                </details>
              </div>
            ))}
          </div>
        </section>

        {showScrollTop && (
          <button
            onClick={scrollToTop}
            aria-label="Back to Top"
            className="hidden md:flex fixed bottom-24 right-8 z-[100] group flex-col items-center gap-3"
          >
            <div className="relative w-[2px] h-16 overflow-hidden bg-white/10 rounded-full">
              <div className="absolute inset-0 w-full h-1/2 bg-gradient-to-t from-transparent via-white to-transparent animate-pulse" />
            </div>
            <div className="w-12 h-12 rounded-full border border-white/10 bg-white/5 backdrop-blur-sm flex items-center justify-center group-hover:bg-white group-hover:scale-110 transition-all duration-500 shadow-2xl">
              <ChevronUp className="w-6 h-6 text-white group-hover:text-black transition-colors duration-300 stroke-[3px]" />
            </div>
          </button>
        )}
      </div>

      <FloatingContactBar onQuoteClick={scrollToQuote} />
      <Footer />
    </PageLayout>
  );
};

export default Solutions;
